import { useEffect, useRef } from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { PaperAirplaneIcon } from '@heroicons/react/24/outline'

import { INTERAX_COLORS } from '../../../../../constants/colors'
import { TESTIDS } from '../../../../../config/testids'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { Input, Spinner } from '../../../../../interaxDesignSystemComponents'
import { CoachMessageBubble } from './CoachMessageBubble'
import type { PendingCoachMessage } from '../useCoachChat'

type CoachChatPanelProps = {
  messages: PendingCoachMessage[]
  /** What the turn itself could not say — e.g. a goal the active-goal cap refused to write. */
  notice?: string
  draft: string
  isBusy: boolean
  /** A concluded conversation is a record: it can be read, but nothing can be added to it. */
  isConcluded: boolean
  onDraftChange: (value: string) => void
  onSend: () => void
  onRetry: (message: PendingCoachMessage) => void
}

export const CoachChatPanel = (props: CoachChatPanelProps) => {
  const { messages, notice, draft, isBusy, isConcluded, onDraftChange, onSend, onRetry } = props
  const { t } = useTranslation()
  const endRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  return (
    <>
      <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
        {messages.map((message) => (
          <CoachMessageBubble key={message.id} message={message} onRetry={onRetry} />
        ))}
        {isBusy && <Spinner />}
        <Box ref={endRef} />
      </Stack>

      {notice && (
        <Typography
          data-testid={TESTIDS.COACH_NOTICE}
          sx={{ color: INTERAX_COLORS.sun, fontSize: 13, mt: 1.5, flexShrink: 0 }}
        >
          {notice}
        </Typography>
      )}

      {isConcluded ? (
        <Typography sx={{ color: INTERAX_COLORS.thunderGrey, fontSize: 13, mt: 2, flexShrink: 0 }}>
          {t('gamer.ariaCoach.conversationConcluded')}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexShrink: 0 }} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <Input
              fullWidth
              value={draft}
              onChange={(event) => onDraftChange(event.target.value)}
              onEnterPress={onSend}
              placeholder={t('gamer.ariaCoach.messagePlaceholder')}
            />
          </Box>
          {/* Sending stays open while she answers: a second message can follow the first. */}
          <IconButton
            onClick={onSend}
            disabled={!draft.trim()}
            aria-label={t('gamer.ariaCoach.send')}
            sx={{
              backgroundColor: INTERAX_COLORS.sun,
              color: INTERAX_COLORS.offBlack,
              '&:hover': { backgroundColor: INTERAX_COLORS.sun },
              '&.Mui-disabled': { backgroundColor: INTERAX_COLORS.white08, color: INTERAX_COLORS.white30 }
            }}
          >
            <PaperAirplaneIcon width={20} />
          </IconButton>
        </Stack>
      )}
    </>
  )
}
